import angular from "angular";
import JSZip from "@progress/jszip-esm";
import { saveAs } from "file-saver";
import { AccountingData, AccountingTargetLogic, CompanyAccountSettings, Dates, ExportMethod, Texts, Configs, Ajax, AccountingTargetSettings, AccountingChannel, Translations } from "@salaxy/core";
import { ExcelHelpers, SessionService, SettingsService, UiHelpers } from "../../services";

/** Locale id */
enum LocaleId {
  Fi = "fi",
  En = "en",
}

/** TargetOption */
interface TargetOptions {
  [key: string]: TargetOption;
}

/** TargetOption */
interface TargetOption {
  /** Export function */
  getExportData?: (accountingData: AccountingData, schemeId?: string, target?: TargetOption) => any[];
  /** List of supported schemes */
  schemes?: { id: string, label: string }[],
  /** Default export method */
  defaultExportMethod?: string;
  /** Boolean indicating if the csv export should contain quoted strings */
  noCsvQuotedStrings?: boolean;

  /** Boolean indicating if the csv export should contain byte order mark */
  noCsvBom?: boolean;

  /** Format data as text*/
  formatAsText?: (data: any[], isFinnish: boolean) => string;

  /** File extension for the text files. If not given, the default extension will be used */
  textFileExtension?: string;

  /** List of dimensions from the settings. */
  dimensions?: string[];
}

/**
 * Provides functionality to export accounting data in various formats.
 */
export class AccountingReportToolsController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "UiHelpers",
    "$timeout",
    "SettingsService",
    "SessionService",
    "$location",
    "$http",
    "AjaxNg1"
  ];

  /** Locale for exports */
  public localeId: string = LocaleId.Fi;

  /**
   * Supported locales for exports
   * TODO: This could be replaced with Culture enum directly in the UI.
   */
  public locales: {
    /** Locale id */
    id: string,
    /** Locale label */
    label: string,
    /** Local description */
    description: string,
  }[] = [
      {
        id: LocaleId.Fi,
        label: Translations.get("SALAXY.ENUM.Culture.fi.label"),
        description: Translations.get("SALAXY.ENUM.Culture.fi.description"),
      },
      {
        id: LocaleId.En,
        label: Translations.get("SALAXY.ENUM.Culture.en.label"),
        description: Translations.get("SALAXY.ENUM.Culture.en.description"),
      },
    ];

  /** Size of the data export */
  public schemeId: string;

  /** Export format */
  public exportMethod: string = ExportMethod.FileExcel;

  /** Supported export formats */
  private _exportMethods: {
    /** File type id */
    id: string,
    /** File type label */
    label: string,
    /** Boolean indicating if the file type supports different locales */
    hasLocales: boolean,
    /** Workflow message after export */
    workflowMessage,
  }[] = [
      {
        id: ExportMethod.FilePdf,
        label: Translations.get("SALAXY.ENUM.ExportMethod.filePdf.label"),
        hasLocales: false,
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.filePdf.workflowMessage"),
      },
      {
        id: ExportMethod.FileExcel,
        label: Translations.get("SALAXY.ENUM.ExportMethod.fileExcel.label"),
        hasLocales: false,
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.fileExcel.workflowMessage"),
      },
      {
        id: ExportMethod.CopyExcel,
        label: Translations.get("SALAXY.ENUM.ExportMethod.copyExcel.label"),
        hasLocales: true,
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.copyExcel.workflowMessage"),
      },
      {
        id: ExportMethod.FileCsv,
        label: Translations.get("SALAXY.ENUM.ExportMethod.fileCsv.label"),
        hasLocales: true,
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.fileCsv.workflowMessage"),
      },
      {
        id: ExportMethod.CopyCsv,
        label: Translations.get("SALAXY.ENUM.ExportMethod.copyCsv.label"),
        hasLocales: true,
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.copyCsv.workflowMessage"),
      },
      {
        id: ExportMethod.FileText,
        label: Translations.get("SALAXY.ENUM.ExportMethod.fileText.label"),
        hasLocales: true,
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.fileText.workflowMessage"),
      },
      {
        id: ExportMethod.CopyText,
        label: Translations.get("SALAXY.ENUM.ExportMethod.copyText.label"),
        hasLocales: true,
        workflowMessage: Translations.get("SALAXY.ENUM.ExportMethod.copyText.workflowMessage"),
      },
      {
        id: ExportMethod.Api,
        label: "API integraatio",
        hasLocales: false,
        workflowMessage: "",
      }
    ];

  /** Export target */
  public targetId = "default";

  /** Targets */
  public targets: any[] = [];

  /**
   * Object with read function to call for retrieving the accounting data.
   * Function has the following argument:  arr, which is an array of accounting data into which the result will be appended.
   */
  public reader: {
    /** Function for reading data into given array. */
    read: (
      /** Target for accounting data */
      target: string,
      /** RuleSet for accounting data */
      ruleSet: string,
      /** Array for accounting data read results */
      arr: AccountingData[],
      /** Message for workflow (loading) */
      message: string,
    ) => Promise<void>,
    /** Optional label for data export */
    exportLabel?: string,
    /** Indicates if the export is not possible */
    disabled: () => boolean,
    /* Indicates if row is expandable */
    showExpand: boolean,
    /* Indicates if rows exists*/
    hasRows: boolean
  };

  /**
   * Indicator for any expanded row
   */
  public anyExpanded: boolean;

  /**
   * Report view table only (partial) or pdf preview (full)
   * Defaults to 'partial'
   */
  public view: "partial" | "full";

  /**
   * Function that is called when the target has been changed
   * Function has the following locals:  targetId: the selected accounting target id.
   */
  public onTargetChange: (params: {
    /** Selected target id */
    targetId: any,
  }) => void;

  /**
   * Function that is called when row expand change
   */
  public onExpandChange: (params: {
    /** Selected target id */
    close: boolean,
  }) => void;

  /** Supported targets */
  private targetOptions: TargetOptions = AccountingTargetLogic.getSupportedTargets();

  private defaultOption: TargetOption = {
    // Palkkaus.fi
    getExportData: (accountingData: AccountingData, schemeId: string, target: TargetOption) => {
      return this.targetOptions["36dff2e2-e181-4f0d-9004-0e4aa92f5e17"].getExportData(accountingData, schemeId, target);
    },
    defaultExportMethod: ExportMethod.FileExcel,
    noCsvBom: false
  };

  private static channelBasePaths = {
    [AccountingChannel.Procountor]: "procountor",
    [AccountingChannel.VismaNetvisor]: "vismanetvisor",
    [AccountingChannel.VismaFivaldi]: "vismafivaldi",
  };

  constructor(
    private uiHelpers: UiHelpers,
    private $timeout: angular.ITimeoutService,
    private settingsService: SettingsService,
    private sessionService: SessionService,
    private $location: angular.ILocationService,
    private $http: angular.IHttpService,
    private ajax: Ajax,
  ) {
  }

  /**
   * Initialize controller values.
   */
  public $onInit() {
    // Get accounting targets
    const getSettings = (): Promise<CompanyAccountSettings> => {
      return new Promise((resolve) => {
        const check = () => {
          if (this.settingsService.current) {
            return resolve(this.settingsService.current);
          }
          this.$timeout(() => check(), 50);
        };

        check();
      });
    };
    getSettings().then((settings) => {
      this.targets = settings.accounting.targets;
      for (const target of this.targets) {
        const targetOption = this.targetOptions[target.format] || this.targetOptions[target.id] || this.defaultOption;
        target.getExportData = targetOption.getExportData;
        target.schemes = targetOption.schemes ?? [];
        target.defaultExportMethod = targetOption.defaultExportMethod;
        target.noCsvQuotedStrings = targetOption.noCsvQuotedStrings;
        target.noCsvBom = targetOption.noCsvBom;
        target.formatAsText = targetOption.formatAsText;
        target.textFileExtension = targetOption.textFileExtension;
        target.dimensions = settings.costAccounting?.dimensions?.map(x => (x.id).toLowerCase()) || [];
      }
      if (settings.accounting.defaultTargetId) {
        this.targetId = settings.accounting.defaultTargetId;
        this.setExportMethod(settings.accounting.defaultTargetId);
      }
    });
    this.view = this.view || "partial";
  }

  /** Exports data in defined format. */
  public export(
    reader: {
      /** Function for reading data into given array. */
      read: (
        /** Target for accounting data */
        target: string,
        /** RuleSet for accounting data */
        ruleSet: string,
        /** Array for accounting data read results */
        arr: AccountingData[],
        /** Workflow loading message */
        message: string,
      ) => Promise<void>,
      /** Optional label for data export */
      exportLabel?: string,
      /** Indicates if the export is not possible */
      disabled: () => boolean,
      /* Indicates if row is expandable */
      showExpand: boolean,
      /* Indicates if rows exists*/
      hasRows: boolean
    } = null,
    /** If true, does not show loading indicator. Default is false. */
    hideLoading = false,
  ): Promise<void> {
    if (!reader) {
      reader = this.reader;
    }
    if (!reader) {
      console.error("No data reader for accounting data.");
      return Promise.resolve();
    }
    const loading = hideLoading ? { dismiss: () => null } : this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");

    const target = this.getTarget(this.targetId);
    const arr: AccountingData[] = [];
    return reader.read(this.targetId, null, arr, this.getWorkflowMessage()).then(() => {
      if (arr.length === 0) {
        loading.dismiss();
        return Promise.resolve();
      }
      switch (this.exportMethod) {
        case ExportMethod.Api:
          loading.dismiss();
          this.sendToApi(target, arr);
          return;
        case ExportMethod.FilePdf:
          if (arr.length === 1) {
            return this.getPdf(arr[0]).then(result => {
              this.uiHelpers.downloadFile(result.blob, result.fileName);
              loading.dismiss();
              return;
            });
          } else {
            const zip = new JSZip();
            const getAllBlobs = (items) => {
              const promises: Array<Promise<{ blob: Blob, fileName: string }>> = [];
              for (const item of items) {
                promises.push(this.getPdf(item));
              }
              return Promise.all(promises).then((results) => {
                let counter = 0;
                results.forEach(item => {
                  counter++;
                  zip.file(`${counter}_ ${item.fileName}`, item.blob);
                });
                return;
              });
            };

            return getAllBlobs(arr).then(() => {
              zip.generateAsync({
                type: "blob",
                mimeType:
                  "application/zip",
              }).then((blob) => {
                saveAs(blob, `${this.getZipName(arr)}.zip`);
                loading.dismiss();
                return;
              });
            });

          }
          break;
        case ExportMethod.FileCsv:
          if (arr.length === 1) {
            const data = arr[0];
            saveAs(new Blob([this.getCsv(target.getExportData(data, this.schemeId, target), this.localeId !== LocaleId.En, !target.noCsvBom, !target.noCsvQuotedStrings)], { type: "application/octet-stream;charset=utf-8" }), `${this.getFileName(data)}.csv`);
            loading.dismiss();
            return;
          } else {
            let counter = 0;
            const zip = new JSZip();
            const next = () => {
              if (counter === arr.length) {
                return;
              }
              const data = arr[counter++];
              const blob = new Blob([this.getCsv(target.getExportData(data, this.schemeId, target), this.localeId !== LocaleId.En, !target.noCsvBom, !target.noCsvQuotedStrings)], { type: "application/octet-stream;charset=utf-8" });
              zip.file(`${this.getFileName(data)}.csv`, blob);
              next();
            };

            next();
            zip.generateAsync({
              type: "blob",
              mimeType:
                "application/zip",
            }).then((blob) => {
              saveAs(blob, `${this.getZipName(arr)}.zip`);
              loading.dismiss();
              return;
            });
          }
          break;
        case ExportMethod.FileExcel:
          if (arr.length === 1) {
            const data = arr[0];
            ExcelHelpers.export(target.getExportData(data, this.schemeId, target), this.getFileName(data)).then(() => {
              loading.dismiss();
              return;
            });
          } else if (arr.length > 1) {
            const tablesData = [];
            const next = () => {
              if (tablesData.length === arr.length) {
                return;
              }
              const data = arr[tablesData.length];
              tablesData.push({
                tableData: target.getExportData(data, this.schemeId, target),
                fileName: this.getFileName(data),
              });
              next();
            };

            next();
            ExcelHelpers.exportMany(tablesData, this.getZipName(arr)).then(() => {
              loading.dismiss();
              return;
            });
          }
          break;
          case ExportMethod.FileText:
            if (arr.length === 1) {
              const data = arr[0];
              saveAs(new Blob([this.getText(target.getExportData(data, this.schemeId, target), this.localeId !== LocaleId.En, target.formatAsText)], { type: "application/octet-stream;charset=utf-8" }), `${this.getFileName(data)}.${target.textFileExtension || "txt"}`);
              loading.dismiss();
              return;
            } else {
              let counter = 0;
              const zip = new JSZip();
              const next = () => {
                if (counter === arr.length) {
                  return;
                }
                const data = arr[counter++];
                const blob = new Blob([this.getText(target.getExportData(data, this.schemeId, target), this.localeId !== LocaleId.En, target.formatAsText)], { type: "application/octet-stream;charset=utf-8" });
                zip.file(`${this.getFileName(data)}.${target.textFileExtension || "txt"}`, blob);
                next();
              };
              next();
              zip.generateAsync({
                type: "blob",
                mimeType:
                  "application/zip",
              }).then((blob) => {
                saveAs(blob, `${this.getZipName(arr)}.zip`);
                loading.dismiss();
                return;
              });
            }
            break;
        case ExportMethod.CopyCsv:
          if (arr.length === 1) {
            const data = arr[0];
            this.copyToClipboard(this.getCsv(target.getExportData(data, this.schemeId, target), this.localeId !== LocaleId.En, !target.noCsvBom, !target.noCsvQuotedStrings));
            loading.dismiss();
            return;
          } else if (arr.length > 1) {
            let counter = 0;
            let csv = !target.noCsvBom ? "\uFEFF" : "";
            const next = () => {
              if (counter === arr.length) {
                return;
              }
              const data = arr[counter++];
              csv += `${this.getFileName(data)}\r\n`;
              csv += this.getCsv(target.getExportData(data, this.schemeId, target), this.localeId !== LocaleId.En, false, !target.noCsvQuotedStrings) + (counter < arr.length ? "\r\n" : "");
              next();
            };
            next();
            this.copyToClipboard(csv);
            loading.dismiss();
            return;
          }
          break;
        case ExportMethod.CopyExcel:
          if (arr.length === 1) {
            const data = arr[0];
            this.copyToClipboard(this.getTabbed(target.getExportData(data, this.schemeId, target), this.localeId !== LocaleId.En));
            loading.dismiss();
            return;
          } else if (arr.length > 1) {
            let counter = 0;
            let tabbed = "";
            const next = () => {
              if (counter === arr.length) {
                return;
              }
              const data = arr[counter++];
              tabbed += `${this.getFileName(data)}\r\n`;
              tabbed += this.getTabbed(target.getExportData(data, this.schemeId, target), this.localeId !== LocaleId.En) + (counter < arr.length ? "\n" : "");
              next();
            };
            next();
            this.copyToClipboard(tabbed);
            loading.dismiss();
            return;
          }
          break;
          case ExportMethod.CopyText:
            if (arr.length === 1) {
              const data = arr[0];
              this.copyToClipboard(this.getText(target.getExportData(data, this.schemeId, target), this.localeId !== LocaleId.En, target.formatAsText));
              loading.dismiss();
              return;
            } else if (arr.length > 1) {
              let counter = 0;
              let txt = "";
              const next = () => {
                if (counter === arr.length) {
                  return;
                }
                const data = arr[counter++];
                txt += `${this.getFileName(data)}\r\n`;
                txt += this.getText(target.getExportData(data, this.schemeId, target), this.localeId !== LocaleId.En, target.formatAsText) + (counter < arr.length ? "\r\n" : "");
                next();
              };
              next();
              this.copyToClipboard(txt);
              loading.dismiss();
              return;
            }
            break;
        default:
          loading.dismiss();
          this.uiHelpers.showAlert("Ei vielä toteutettu", "Tätä tiedostotyyppiä ei ole toteutettu.");
          return;
      }
      loading.dismiss();
      return;
    });
  }

  /** Navigates to given path and closes possible open modal dialogs. */
  public navigate(path: string) {
    this.$location.path(path);
    this.uiHelpers.dismissAllModals();
  }

  /**
   * Change target and call event handler.
   * @param targetId - New target id.
   */
  public changeTarget(targetId: string) {
    this.setExportMethod(targetId);
    this.targetId = targetId;
    this.onTargetChange({ targetId });
  }

  /**
   * Returns the export methods that are available for the accounting target.
   */
  public get exportMethods() {
    // If an accounting target from company settings is selected...
    const target = this.getTarget(this.targetId);
    // ...and available methods for the target are configured
    const availableMethods = [];
    if (target?.availableMethods?.length > 0) {
      // Filter the possible export methods according to the available methods configured for the target.
      availableMethods.push(...target.availableMethods);
    } else {
      // Defaults only
      availableMethods.push(...
        [
          ExportMethod.FilePdf,
          ExportMethod.FileExcel,
          ExportMethod.CopyExcel,
          ExportMethod.FileCsv,
          ExportMethod.CopyCsv
        ]);
    }
    return this._exportMethods.filter(v => availableMethods.indexOf(v.id as ExportMethod) > -1);
  }

  /**
   * Returns the export method type definition
   * @param exportMethodId export method id
   */
  public getExportMethod(exportMethodId: string) {
    return this.exportMethods.find((x) => x.id === exportMethodId);
  }

  /**
   * Returns the target definition
   * @param targetId Target id.
   */
  public getTarget(targetId: string) {
    return this.targets.find((x) => x.id === targetId);
  }

  /**
   * Returns the locale definition
   * @param localeId Locale id.
   */
  public getLocale(localeId: string) {
    return this.locales.find((x) => x.id === localeId);
  }

  /**
   * Returns the scheme definition.
   * @param schemeId Scheme id.
   */
  public getScheme(schemeId: string) {
    return this.getTarget(this.targetId)?.schemes.find(x => x.id === schemeId);
  }
  private setExportMethod(targetId: string) {

    // First check if the targetId is one of the "default" target options
    if (this.targetOptions[targetId]) {
      this.exportMethod = this.targetOptions[targetId].defaultExportMethod;
      this.schemeId = this.targetOptions[targetId].schemes?.length ? this.targetOptions[targetId].schemes[0].id : null;
      return;
    }

    const target = this.getTarget(targetId);
    this.schemeId = target.schemes?.length ? target.schemes[0].id : null;
    this.exportMethod = (target.method && target.method !== "undefined") ? target.method : target.defaultExportMethod;
  }

  /**
   * Method for calling onExpandChange event
   * @param close If true, closes the expanding component.
   */
  public expand(close = false) {
    this.onExpandChange({ close });
  }

  private getFileName(data: AccountingData) {
    return Texts.escapeFileName(`${Translations.get("SALAXY.UI_Terms.downLoadableMaterialTypes.accounting")}_${data.employer.displayName}_${Dates.format((data.period.start ?? "today"),"YYYY_MM")}`);
  }

  private getZipName(all: AccountingData[]) { // eslint-disable-line @typescript-eslint/no-unused-vars
    return Texts.escapeFileName(`${Translations.get("SALAXY.UI_Terms.downLoadableMaterialTypes.accounting")}_${Dates.format("today","YYYYMMDD")}`);
  }

  private getWorkflowMessage() {
    const exportMethod = this.getExportMethod(this.exportMethod);
    if (exportMethod) {
      return exportMethod.workflowMessage;
    }
    return null;
  }

  private getCsv(data: any[], isFinnish = true, addPrefix: boolean, addQuotes: boolean) {
    const separator = (isFinnish ? ";" : ",");
    const rowMapper = this.formatRow(isFinnish, separator, addQuotes);
    const rows = data.map(rowMapper);
    return (addPrefix ? "\uFEFF" : "") + rows.join("\r\n");
  }

  private getText(data: any[], isFinnish = true, formatAsText: (data: any[], isFinnish: boolean) => string) {
    if (formatAsText == null) {
      const rowMapper = this.formatRow(isFinnish, "\t", false);
      const rows = data.map(rowMapper);
      return rows.join("\r\n");
    }
    else {
      return formatAsText(data, isFinnish);
    }
  }

  private getPdf(obj): Promise<{ blob: Blob, fileName: string }> {
    const url = `${Configs.current.reportServer}/pdf?id=&reportType=${encodeURIComponent("accountingReport")}&template=${encodeURIComponent("accountingReport")}&disposition=inline&access_token=${this.ajax.getCurrentToken()}`;
    return (this.$http.post(url, { object: obj }, { responseType: "blob" }) as Promise<any>).then((response) => {
      if (response.data) {
        return Promise.resolve({ blob: response.data, fileName: response.headers("content-disposition").split(";")[1].split("filename")[1].split("=")[1].trim().replace(/"/g, "") });
      }
    }, (error) => {
      console.error(error);
      return Promise.reject(error);
    });
  }
  private getTabbed(data: any[], isFinnish = true) {
    const rowMapper = this.formatRow(isFinnish, "\t");
    const rows = data.map(rowMapper);
    return rows.join("\n");
  }

  private formatRow(isFinnish: boolean, separator: string, addQuotes = true) {
    const fieldMapper = this.formatField(isFinnish, addQuotes);
    return (row) => {
      return row.map(fieldMapper).join(separator);
    };
  }

  private formatField(isFinnish: boolean, addQuotes = true) {
    return (value) => {
      if (value == null) { // we want to catch anything null-ish, hence just == not ===
        return "";
      }
      if (typeof (value) === "number") {
        if (isFinnish) {
          return value.toString().replace(".", ",");
        }
        return value;
      }
      if (typeof (value) === "boolean") {
        return (value ? "TRUE" : "FALSE");
      }
      if (typeof (value) === "string") {
        if (addQuotes) {
          return "\"" + value.replace(/"/g, "\"\"") + "\"";
        }
        return value;
      }
      if (typeof (value) === "object") {
        if (value instanceof Date) {
          if (isFinnish) {
            return Dates.format(value,"D.M.YYYY");
          } else {
            return Dates.format(value,"YYYY-MM-DD");
          }
        } else {
          if (addQuotes) {
            return "\"" + JSON.stringify(value).replace(/"/g, "\"\"") + "\"";
          }
          return JSON.stringify(value);
        }
      }
      return JSON.stringify(value);
    };
  }

  private copyToClipboard(data: string) {
    (navigator as any).clipboard.writeText(data);
  }

  private async sendToApi(target: AccountingTargetSettings, arr: any[]): Promise<void> {
    if(!target.channel || target.channel === "undefined") {
      return;
    }

    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    const items = [];
    for(const accountingData of arr) {
      items.push( {
        accountingData,
        preview: (await this.$http.post(this.getAccountingDataUrl(target, "accountingPreview"), accountingData, { responseType: "json", }))?.data,
        result: "",
        error: "",
      });
    }

    loading.dismiss();
    this.uiHelpers.openEditDialog("salaxy-components/report/modals/PostAccountingData.html", items,{
      postData: (item) => {
        item.isPosting = true;
        this.$http.post(this.getAccountingDataUrl(target, "accounting"), item.accountingData, { responseType: "json", })
          .then((result) => {
            item.isPosting = false;
            item.error = "";
            item.result = this.getAccountingDataResult(target, result);
          })
          .catch( (err) => {
            item.isPosting = false;
            item.error =  this.getAccountingDataError(target, err);
            item.result = "";
          });
      },
      previewTemplateUrl: this.getAccountingDataPreviewTemplate(target),
    },"lg");
  }

  private getAccountingDataUrl(target: AccountingTargetSettings, path: string): string {
    const accessToken = this.ajax.getCurrentToken();
    const url = `${Configs.current.integrationsServer}/${AccountingReportToolsController.channelBasePaths[target.channel]}/${path}`;
    return `${url}?accessToken=${accessToken}&accountingTargetId=${target.id}`;
  }

  private getAccountingDataError(target: AccountingTargetSettings, error: any) {
    if (error?.data?.detail) {
      return Translations.get("SALAXY.UI_Terms.error") +", " + error.data.detail;
    }
    if (error.data) {
      return  Translations.get("SALAXY.UI_Terms.error") + ", " + error.data.status + "-" + error.data.title;
    }
    return Translations.get("SALAXY.UI_Terms.error");
  }

  private getAccountingDataResult(target: AccountingTargetSettings, result: any) {
    switch(target.channel) {
      case AccountingChannel.VismaFivaldi:
        return Translations.get("SALAXY.UI_Terms.ok") + ", VismaFivaldi: " + result.data.fivaldiVoucherNumber;
      default:
        return Translations.get("SALAXY.UI_Terms.ok");
    }
  }

  private getAccountingDataPreviewTemplate(target: AccountingTargetSettings) {
    switch(target.channel) {
      case AccountingChannel.VismaFivaldi:
        return "salaxy-components/report/modals/FivaldiPreview.html";
      default:
        return "";
    }
  }
}
